import { useNavigate, Link } from "react-router-dom";
import {
  Home, User, Building2, Users, LogOut, ShieldAlert,
  ArrowRight, CheckCircle2, LayoutDashboard, BookOpen,
  FileCheck, BarChart3, Settings, CreditCard, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import type { UserRole } from "@/types/database";

// ─── Role definitions ─────────────────────────────────────────────────────────

const ROLES: {
  id: UserRole;
  label: string;
  price: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  ring: string;
  description: string;
  features: string[];
  quickLinks: { label: string; path: string; icon: React.ElementType }[];
}[] = [
  {
    id: "host",
    label: "STR Host",
    price: "Free forever",
    icon: Home,
    color: "text-emerald-600",
    bg: "bg-emerald-500/10",
    ring: "ring-emerald-500/30",
    description: "Independent STR hosts managing their own properties without a council or PM.",
    features: ["Booking management", "Guest portal", "Document uploads", "Maintenance log"],
    quickLinks: [
      { label: "Host Dashboard", path: "/host-dashboard", icon: LayoutDashboard },
      { label: "Bookings",       path: "/bookings",       icon: BookOpen },
      { label: "Documents",      path: "/my-documents",   icon: FileCheck },
    ],
  },
  {
    id: "individual",
    label: "Individual",
    price: "$9.99 / mo",
    icon: User,
    color: "text-blue-600",
    bg: "bg-blue-500/10",
    ring: "ring-blue-500/30",
    description: "Independent landlords managing up to 5 properties — income tracking, calendar, CSV exports.",
    features: ["Rental calendar", "Income & expense tracker", "CSV export", "Maintenance log"],
    quickLinks: [
      { label: "My Dashboard", path: "/individual-dashboard", icon: LayoutDashboard },
      { label: "Bookings",     path: "/my-bookings",          icon: BookOpen },
      { label: "Billing",      path: "/account/billing",      icon: CreditCard },
    ],
  },
  {
    id: "strata_admin",
    label: "Strata Admin",
    price: "$18.99 / mo",
    icon: Building2,
    color: "text-violet-600",
    bg: "bg-violet-500/10",
    ring: "ring-violet-500/30",
    description: "Strata councils self-managing STR compliance, booking approvals, and host communications.",
    features: ["By-law enforcement", "Booking approvals", "Dispute tracking", "Night-limit rules"],
    quickLinks: [
      { label: "Dashboard",   path: "/dashboard",      icon: LayoutDashboard },
      { label: "Compliance",  path: "/compliance",     icon: FileCheck },
      { label: "Reports",     path: "/reports",        icon: BarChart3 },
      { label: "Strata Setup",path: "/strata-config",  icon: Settings },
    ],
  },
  {
    id: "property_manager",
    label: "Property Manager",
    price: "$28.99 / building / mo",
    icon: Users,
    color: "text-orange-600",
    bg: "bg-orange-500/10",
    ring: "ring-orange-500/30",
    description: "Property management firms handling compliance across multiple strata buildings.",
    features: ["Multi-building view", "Daily digest emails", "Team management", "Cross-building analytics"],
    quickLinks: [
      { label: "Dashboard",  path: "/dashboard", icon: LayoutDashboard },
      { label: "Reports",    path: "/reports",   icon: BarChart3 },
      { label: "Team",       path: "/team",      icon: Users },
      { label: "Billing",    path: "/account/billing", icon: CreditCard },
    ],
  },
];

// ─── Admin Portal ─────────────────────────────────────────────────────────────

export default function AdminPortal() {
  const { user, devRole, setDevRole, logout } = useAuth();
  const navigate = useNavigate();

  // Gate: only system_admin may access this page
  if (user && user.role !== "system_admin") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="text-center space-y-3 max-w-sm">
          <ShieldAlert className="h-10 w-10 text-destructive mx-auto" />
          <h1 className="text-lg font-bold">Access Denied</h1>
          <p className="text-sm text-muted-foreground">This portal is restricted to system administrators.</p>
          <Button variant="outline" onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  const enterRole = (role: UserRole) => {
    setDevRole(role);
    navigate("/dashboard");
  };

  const clearRole = () => {
    setDevRole(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin-login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Stratakey" className="h-7 w-auto rounded-lg object-contain" />
              <span className="text-sm font-semibold text-foreground/80">Stratakey</span>
            </Link>
            <div className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5">
              <ShieldAlert className="h-3 w-3 text-amber-500" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600">Admin Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block">{user?.email}</span>
            <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={handleLogout}>
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 space-y-8">

        {/* ── Page title ──────────────────────────────────────────── */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Role Testing Panel</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enter any account type to preview its full UX. Your real identity ({user?.fullName}) is never changed.
          </p>
        </div>

        {/* ── Current impersonation status ────────────────────────── */}
        {devRole ? (
          <div className="flex items-center gap-3 rounded-lg border border-orange-500/30 bg-orange-500/8 px-4 py-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500/15">
              <CheckCircle2 className="h-4 w-4 text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                Currently viewing as{" "}
                <span className="text-orange-600 font-semibold capitalize">{devRole.replace("_", " ")}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                All dashboards and routes reflect this role. Click any plan card below to switch.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate("/dashboard")}>
                Open Dashboard
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={clearRole}
                title="Clear impersonation"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            No role selected — click a card below to enter that account type.
          </div>
        )}

        {/* ── Role cards ──────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {ROLES.map((role) => {
            const Icon      = role.icon;
            const isActive  = devRole === role.id;

            return (
              <div
                key={role.id}
                className={`
                  relative flex flex-col rounded-xl border bg-card p-5 transition-all
                  ${isActive
                    ? `ring-2 ${role.ring} shadow-lg shadow-primary/5`
                    : "hover:border-primary/30 hover:shadow-md"}
                `}
              >
                {isActive && (
                  <div className="absolute right-3 top-3">
                    <Badge className="h-4 px-1.5 text-[9px] bg-primary text-primary-foreground">Active</Badge>
                  </div>
                )}

                {/* Icon + title */}
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${role.bg} mb-4`}>
                  <Icon className={`h-5 w-5 ${role.color}`} />
                </div>
                <h3 className="text-sm font-bold mb-0.5">{role.label}</h3>
                <p className={`text-xs font-medium mb-3 ${role.color}`}>{role.price}</p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-4 flex-1">
                  {role.description}
                </p>

                {/* Feature bullets */}
                <ul className="space-y-1 mb-5">
                  {role.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <CheckCircle2 className="h-2.5 w-2.5 text-emerald-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Enter button */}
                <Button
                  size="sm"
                  className={`w-full text-xs ${isActive ? "" : "variant-outline"}`}
                  variant={isActive ? "default" : "outline"}
                  onClick={() => enterRole(role.id)}
                >
                  {isActive ? "Re-enter as " : "Enter as "}{role.label}
                  <ArrowRight className="ml-1.5 h-3 w-3" />
                </Button>

                {/* Quick nav links (only when this role is active) */}
                {isActive && role.quickLinks.length > 0 && (
                  <div className="mt-3 pt-3 border-t space-y-1">
                    <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide mb-1.5">Quick nav</p>
                    {role.quickLinks.map((link) => {
                      const LinkIcon = link.icon;
                      return (
                        <button
                          key={link.path}
                          type="button"
                          onClick={() => navigate(link.path)}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-left"
                        >
                          <LinkIcon className="h-3 w-3 shrink-0" />
                          {link.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Info footer ─────────────────────────────────────────── */}
        <div className="rounded-lg border bg-muted/30 px-5 py-4 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground text-sm">How this works</p>
          <p>Entering a role sets a temporary view — your actual account remains <strong>system_admin</strong>. All routes and dashboards render as if you were that user type, but subscription gates are bypassed so you can access every screen.</p>
          <p>Switching back clears the view. You can also use the <strong>"Viewing as…" pill</strong> in the top header to switch roles while browsing the app.</p>
        </div>
      </main>
    </div>
  );
}
